import React, { useState, useRef } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Stack, 
  CircularProgress, Avatar, Chip, useMediaQuery
} from '@mui/material';
import { ScheduleOutlined as ScheduleIcon, BoltOutlined as FlashOnIcon } from '@mui/icons-material';
import { useTheme } from '@emotion/react';
import { useFullHeightPageFix } from 'src/utils/useFullHeightPageFix.js';
import { StarsSelect } from 'src/components/blocks/StarsSelect.js';
import { Helmet } from 'react-helmet';
import { useNavigate } from 'react-router-dom';
import MaestroNavbar from './blocks/MaestroNavbar.js';
import WeekPicker from './blocks/WeekPicker.js';
import MaestroSideTray from './blocks/MaestroSideTray.js';
import TrayToggleButton from './blocks/TrayToggleButton.js';

const getPerformanceColor = (value, theme, isFuture = false) => {
  if (isFuture || value === null) return theme.palette.text.disabled || '#999999'; // Gray for future/unknown
  if (value >= 80) return theme.palette.success?.main || '#4caf50'; // Green
  if (value >= 60) return theme.palette.warning?.main || '#ff9800'; // Yellow/Orange
  return theme.palette.error?.main || '#f44336'; // Red
};

const CircularProgressWithLabel = ({ value, label, isFuture = false }) => {
  const theme = useTheme();
  const progressColor = getPerformanceColor(value, theme, isFuture);
  const displayValue = isFuture || value === null ? 0 : value;
  const displayText = isFuture || value === null ? '--' : `${Math.round(value)}%`;
  
  return (
    <Box sx={{ position: 'relative', display: 'inline-flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ position: 'relative' }}>
        <CircularProgress
          variant="determinate"
          value={100}
          size={120}
          thickness={4}
          sx={{ 
            color: theme.palette.grey[200],
            position: 'absolute'
          }}
        />
        <CircularProgress
          variant="determinate"
          value={displayValue}
          size={120}
          thickness={4}
          sx={{ 
            color: progressColor,
            '& .MuiCircularProgress-circle': {
              strokeLinecap: 'round',
            }
          }}
        />
        <Box
          sx={{
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column'
          }}
        >
          <Typography variant="h4" component="div" color="text.primary" fontWeight="bold">
            {displayText}
          </Typography>
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', maxWidth: 120 }}>
        {label}
      </Typography>
    </Box>
  );
};

const ActionCard = ({ title, subtitle, onClick, children, variant = "outlined" }) => {
  const theme = useTheme();
  
  return (
    <Card 
      variant="outlined"
      sx={{ 
        cursor: 'pointer',
        transition: 'all 0.2s ease-in-out',
        borderRadius: "20px",
        boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
        backgroundColor: theme.palette.background.paper,
        border: "none",
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[12]
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {subtitle}
          </Typography>
        )}
        {children}
      </CardContent>
    </Card>
  );
};

const Dashboard = () => {
  const [lessonPlanStars, setLessonPlanStars] = useState(0);
  const containerRef = useRef(null);
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sideTrayOpen, setSideTrayOpen] = useState(!isMobile);
  
  useFullHeightPageFix(containerRef);

  const today = new Date();
  
  const isWeekend = (date) => {
    const dayOfWeek = date.getDay();
    return dayOfWeek === 0 || dayOfWeek === 6;
  };

  const findNextWeekday = (startDate, direction) => {
    const newDate = new Date(startDate);
    do {
      newDate.setDate(newDate.getDate() + direction);
    } while (isWeekend(newDate));
    return newDate;
  };

  const countWeekdaysBetween = (startDate, endDate) => {
    if (startDate.toDateString() === endDate.toDateString()) return 0;
    
    let count = 0;
    let current = new Date(startDate);
    const direction = endDate > startDate ? 1 : -1;
    
    while (current.toDateString() !== endDate.toDateString()) {
      current = findNextWeekday(current, direction);
      count++;
    }
    
    return direction === 1 ? count : -count;
  };

  const getInitialDate = () => {
    if (isWeekend(today)) {
      return findNextWeekday(today, 1);
    }
    return today;
  };

  const [selectedDate, setSelectedDate] = useState(getInitialDate());

  const handleDateChange = (newDate) => {
    setSelectedDate(newDate);
  };

  // Calculate if selected date is in the past, present, or future (based on actual today)
  const daysDifference = Math.ceil((selectedDate - today) / (1000 * 60 * 60 * 24));
  const weekdaysDifference = countWeekdaysBetween(today, selectedDate);
  const isToday = selectedDate.toDateString() === getInitialDate().toDateString();
  const isPast = daysDifference < 0;
  const isFuture = daysDifference > 0;

  // Calculate which day of the unit this represents (1-5) based on weekdays
  // -2 → Day 1, -1 → Day 2, 0 → Day 3, +1 → Day 4, +2 → Day 5
  const unitDay = weekdaysDifference + 3;

  // Generate mock performance data based on date
  const getPerformanceData = (date) => {
    if (isFuture) {
      return { classroomPerformance: null, benchmarkPercentile: null };
    }
    
    // Generate consistent but varied data for past/current school dates
    const seed = date.getDate() + date.getMonth() * 31;
    const classroom = Math.max(30, Math.min(95, 65 + (seed % 20) - 10));
    const benchmark = Math.max(25, Math.min(90, 55 + (seed % 25) - 12));
    
    return {
      classroomPerformance: classroom,
      benchmarkPercentile: benchmark
    };
  };

  const { classroomPerformance, benchmarkPercentile } = getPerformanceData(selectedDate);


  const handleCustomizePlan = () => {
    navigate('/chat');
  };

  const handleScheduleMentor = () => {
    // TODO: Implement mentor scheduling
    console.log('Schedule with mentor clicked');
  };

  const handleUpdateAgenda = () => {
    // TODO: Implement agenda update
    console.log('Update agenda clicked');
  };

  const handleLessonPlan = () => {
    // Navigate to detailed lesson plan page with unit day
    navigate(`/lesson-plan/${unitDay}`, { 
      state: { 
        date: selectedDate.toISOString().split('T')[0],
        unitDay: unitDay,
        isToday: isToday
      } 
    });
  };

  const handleNewChat = () => {
    navigate('/chat');
  };

  const toggleSideTray = () => {
    setSideTrayOpen(!sideTrayOpen);
  };



  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Helmet>
        <title>Maestro - Dashboard</title>
        <meta name="description" content="AI Teaching Assistant dashboard for performance tracking and lesson planning." />
      </Helmet>
      
      {/* Side Tray */}
      <MaestroSideTray 
        open={sideTrayOpen}
        onClose={() => setSideTrayOpen(false)}
        onNewChat={handleNewChat}
      />
      
      {/* Toggle Button (only visible when tray is closed or on mobile) */}
      {(!sideTrayOpen || isMobile) && (
        <TrayToggleButton 
          onClick={toggleSideTray}
          open={sideTrayOpen}
        />
      )}

      {/* Main Content */}
      <Box 
        ref={containerRef} 
        sx={{ 
          display: 'flex', 
          flexDirection: 'column', 
          height: '100vh', 
          overflow: 'auto',
          flexGrow: 1,
          width: sideTrayOpen && !isMobile ? 'calc(100% - 300px)' : '100%',
          transition: theme.transitions.create(['margin-left', 'width'], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),
          alignItems: 'center',
          justifyContent: 'flex-start'
        }}
      >
        <MaestroNavbar />
      
      <Box 
        display="flex" 
        flexDirection="column" 
        sx={{ 
          width: "100%",
          maxWidth: { xs: "100%", sm: "600px" }, 
          p: 3,
          my: 2,
          borderRadius: "20px",
          boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
          backgroundColor: theme.palette.background.paper
        }}
      >
        <WeekPicker 
          selectedWeek={selectedDate}
          onWeekChange={handleDateChange}
        />

        {/* Header with Progress Circles */}
        <Box sx={{ mb: 2 }}>
          <Stack direction="row" spacing={4} justifyContent="center" sx={{ mb: 3 }}>
            <CircularProgressWithLabel 
              value={classroomPerformance} 
              label="CLASSROOM PERFORMANCE"
              isFuture={isFuture}
            />
            <CircularProgressWithLabel 
              value={benchmarkPercentile} 
              label="PERCENTILE VS BENCHMARKS"
              isFuture={isFuture}
            />
          </Stack>
        </Box>
        {/* Action Cards */}
        <Stack spacing={2}>
          {/* This Week's Lesson Plans */}
          <Card 
            variant="outlined"
            sx={{
              borderRadius: "20px",
              boxShadow: theme.shadows.cardLg ? theme.shadows.cardLg(theme.palette, theme.palette.mode) : theme.shadows[8],
              backgroundColor: theme.palette.background.paper,
              border: "none"
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500, color: 'text.secondary', mb: 2 }}>
                THIS WEEK'S LESSON PLANS
              </Typography>
              
              <Stack spacing={2}>
                <ActionCard 
                  title="Unit 5.0 - Day 1 →"
                  subtitle="Monday's lesson plan"
                  onClick={() => navigate('/lesson-plan/1')}
                />
                <ActionCard 
                  title="Unit 5.0 - Day 2 →"
                  subtitle="Tuesday's lesson plan"
                  onClick={() => navigate('/lesson-plan/2')}
                />
                <ActionCard 
                  title="Unit 5.0 - Day 3 →"
                  subtitle="Wednesday's lesson plan"
                  onClick={() => navigate('/lesson-plan/3')}
                />
                <ActionCard 
                  title="Unit 5.0 - Day 4 →"
                  subtitle="Thursday's lesson plan"
                  onClick={() => navigate('/lesson-plan/4')}
                />
                <ActionCard 
                  title="Unit 5.0 - Day 5 →"
                  subtitle="Friday's lesson plan"
                  onClick={() => navigate('/lesson-plan/5')}
                />
              </Stack>
            </CardContent>
          </Card>
        </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Dashboard; 